import { Client, ClientSetup } from '../../models/client';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { CompaniesService } from '../../services/companies.service';
import { Injectable } from '@angular/core';
import { CompanyLocationConfig } from '../../models/company-location-config';
import { ChangeRequestService } from '../../services/change-request.service';
import { AERequestStatusType, ChangeRequestHistoryEntry } from '../../models/change-requests';
import { ClientTimeOffRequestService } from '../../services/time-off-request.service';

export class SetAvailableClients {
  static readonly type = '[ClientSelection] Set Available Clients';
  constructor(public clients: Client[]) {}
}

export class ClientSelected {
  static readonly type = '[ClientSelection] Client Selected';
  constructor(public clientCode: string) {}
}

export class ClearClientState {
  static readonly type = '[ClientSelection] Clear Selected';
}

export class ClientSelectionLoadOpenChangeRequests {
  static readonly type = '[ClientSelection] Load Open Change Requests';
  constructor() {}
}

export interface IClientSelectionState {
  clientCode: string;
  client: Client;
  clientSetup: ClientSetup;
  availableClients: Client[] | null;
  locations: CompanyLocationConfig[];
  canEnableChangeRequests: boolean;
  canEnableTimeOff: boolean;
  openChangeRequests: ChangeRequestHistoryEntry[];
}

@State<IClientSelectionState>({
  name: 'clientSelection',
  defaults: {
    client: null,
    clientSetup: null,
    clientCode: null,
    availableClients: [],
    locations: [],
    canEnableChangeRequests: false,
    canEnableTimeOff: false,
    openChangeRequests: []
  }
})
@Injectable()
export class ClientSelectionState {
  constructor(
    protected companiesService: CompaniesService,
    protected changeRequestService: ChangeRequestService,
    protected clientTimeOffService: ClientTimeOffRequestService
  ) {}

  @Selector()
  static availableClients(state: IClientSelectionState) {
    const clonedList = structuredClone(state.availableClients);

    return clonedList.sort((a, b) => this._sortClients(a, b));
  }
  @Selector()
  static shouldShowClientSwitcher(state: IClientSelectionState) {
    return state.availableClients.length > 1;
  }
  @Selector()
  static clientCode(state: IClientSelectionState) {
    return state.clientCode;
  }
  @Selector()
  static clientSetup(state: IClientSelectionState) {
    return state.clientSetup;
  }
  @Selector()
  static client(state: IClientSelectionState) {
    return state.client;
  }
  @Selector()
  static locations(state: IClientSelectionState) {
    return state.locations;
  }
  @Selector()
  static canEnableChangeRequests(state: IClientSelectionState) {
    return state.canEnableChangeRequests;
  }

  @Selector()
  static getOpenChangeRequests(state: IClientSelectionState) {
    return state.openChangeRequests;
  }

  @Selector()
  static canEnableTimeOff(state: IClientSelectionState) {
    return state.canEnableTimeOff;
  }

  @Selector()
  static hasTimeclockSetup(state: IClientSelectionState) {
    const clientSetup = state.clientSetup;
    return clientSetup != null && clientSetup.TimeclockVendorId == 'SC';
  }

  @Action(SetAvailableClients)
  setAvailableClients(ctx: StateContext<IClientSelectionState>, { clients }: SetAvailableClients) {
    ctx.patchState({
      availableClients: clients
    });
  }

  @Action(ClientSelected)
  clientSelected(ctx: StateContext<IClientSelectionState>, { clientCode }: ClientSelected) {
    const stateSnapshot = ctx.getState();
    if (stateSnapshot.clientCode != clientCode) {
      let companyData: Observable<Client>;
      if (stateSnapshot.availableClients.some(x => x.Id == clientCode)) {
        //The client data for the client we're interested in has already been fetched.
        const data = stateSnapshot.availableClients.find(x => x.Id == clientCode);
        companyData = of(data);
      } else {
        //We don't have company data. Fetch it.
        companyData = this.companiesService.getCompany(clientCode);
      }
      const clientSetup = this.companiesService.getClientSetup(clientCode);
      const locations = this.companiesService.getLocations(clientCode);

      const changeRequests = this.changeRequestService
        .canEnableChangeRequests(clientCode)
        .pipe(map(canEnable => canEnable.Value));
      const timeOff = this.clientTimeOffService.canEnable(clientCode);
      return forkJoin([companyData, clientSetup, locations, changeRequests, timeOff]).pipe(
        tap(results => {
          ctx.patchState({
            clientCode: clientCode,
            client: results[0],
            clientSetup: results[1],
            locations: results[2],
            canEnableChangeRequests: results[3],
            canEnableTimeOff: results[4].Value
          });
        })
      );
    }
    return of();
  }

  @Action(ClientSelectionLoadOpenChangeRequests)
  loadOpenChangeRequests(ctx: StateContext<IClientSelectionState>) {
    const state = ctx.getState();
    if (state.canEnableChangeRequests) {
      return this.changeRequestService
        .getRequestsApprover(state.clientCode, AERequestStatusType.Open)
        .pipe(
          tap(requests => {
            ctx.patchState({
              openChangeRequests: requests
            });
          })
        );
    } else {
      return of();
    }
  }

  @Action(ClearClientState)
  clearSelected(ctx: StateContext<IClientSelectionState>) {
    ctx.setState({
      clientCode: null,
      client: null,
      clientSetup: null,
      availableClients: [],
      locations: [],
      canEnableChangeRequests: false,
      canEnableTimeOff: false,
      openChangeRequests: []
    });
  }

  private static _sortClients(a: Client, b: Client) {
    const aName = this._getClientName(a);
    const bName = this._getClientName(b);
    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  }

  private static _getClientName(client: Client) {
    if (client.DBA != null && client.DBA != '') {
      return client.DBA;
    }
    return client.Name;
  }
}
